import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFileDownload, FaCheckCircle, FaBuilding } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionRentaService from '../../services/declaracionRentaService';
import '../../styles/components/DJ1835BienesRaicesTab.css';

/**
 * Componente para gestionar DJ1835 - Bienes Raíces Arrendados
 *
 * Permite crear, editar, eliminar registros y generar CSV con formato SII
 * Diseñado para MCP (Model Context Protocol)
 *
 * @param {Object} props
 * @param {number} props.declaracionId - ID de la declaración jurada
 * @param {number} props.year - Año de la declaración
 * @param {number} props.idCompany - ID de la empresa
 * @param {string} props.rutEmpresa - RUT de la empresa
 * @param {string} props.token - Token de autenticación
 */
const DJ1835BienesRaicesTab = ({
    declaracionId,
    year,
    idCompany,
    rutEmpresa,
    token
}) => {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRegistro, setEditingRegistro] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    const [formData, setFormData] = useState({
        rol_bien_raiz: '',
        comuna_propiedad: '',
        rut_arrendador: '',
        rut_arrendatario: '',
        monto_arriendo: '',
        enero: false,
        febrero: false,
        marzo: false,
        abril: false,
        mayo: false,
        junio: false,
        julio: false,
        agosto: false,
        septiembre: false,
        octubre: false,
        noviembre: false,
        diciembre: false,
        amoblado: false,
        destino_arriendo: '',
        marque_con_x: '',
        naturaleza_bien_raiz: ''
    });

    // Cargar registros al montar el componente
    useEffect(() => {
        loadRegistros();
    }, [declaracionId]);

    const loadRegistros = async () => {
        try {
            setLoading(true);
            const data = await declaracionRentaService.getBienesRaicesArrendados(
                declaracionId,
                idCompany,
                token
            );
            setRegistros(data);
        } catch (error) {
            console.error('Error al cargar registros:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los registros'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (registro = null) => {
        if (registro) {
            setEditingRegistro(registro);
            setFormData({
                rol_bien_raiz: registro.rol_bien_raiz || '',
                comuna_propiedad: registro.comuna_propiedad || '',
                rut_arrendador: registro.rut_arrendador || '',
                rut_arrendatario: registro.rut_arrendatario || '',
                monto_arriendo: registro.monto_arriendo || '',
                enero: registro.enero || false,
                febrero: registro.febrero || false,
                marzo: registro.marzo || false,
                abril: registro.abril || false,
                mayo: registro.mayo || false,
                junio: registro.junio || false,
                julio: registro.julio || false,
                agosto: registro.agosto || false,
                septiembre: registro.septiembre || false,
                octubre: registro.octubre || false,
                noviembre: registro.noviembre || false,
                diciembre: registro.diciembre || false,
                amoblado: registro.amoblado || false,
                destino_arriendo: registro.destino_arriendo || '',
                marque_con_x: registro.marque_con_x || '',
                naturaleza_bien_raiz: registro.naturaleza_bien_raiz || ''
            });
        } else {
            setEditingRegistro(null);
            setFormData({
                rol_bien_raiz: '',
                comuna_propiedad: '',
                rut_arrendador: '',
                rut_arrendatario: '',
                monto_arriendo: '',
                enero: false,
                febrero: false,
                marzo: false,
                abril: false,
                mayo: false,
                junio: false,
                julio: false,
                agosto: false,
                septiembre: false,
                octubre: false,
                noviembre: false,
                diciembre: false,
                amoblado: false,
                destino_arriendo: '',
                marque_con_x: '',
                naturaleza_bien_raiz: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRegistro(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!formData.rol_bien_raiz || !formData.comuna_propiedad ||
            !formData.rut_arrendador || !formData.rut_arrendatario ||
            !formData.monto_arriendo) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos requeridos',
                text: 'Debe completar todos los campos obligatorios'
            });
            return;
        }

        try {
            if (editingRegistro) {
                await declaracionRentaService.updateBienRaizArrendado(
                    editingRegistro.id,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Registro actualizado exitosamente'
                });
            } else {
                await declaracionRentaService.createBienRaizArrendado(
                    declaracionId,
                    formData,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Registro creado exitosamente'
                });
            }
            handleCloseModal();
            loadRegistros();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al guardar registro'
            });
        }
    };

    const handleDelete = async (registro) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await declaracionRentaService.deleteBienRaizArrendado(
                    registro.id,
                    idCompany,
                    token
                );
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'Registro eliminado exitosamente'
                });
                loadRegistros();
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Error al eliminar registro'
                });
            }
        }
    };

    const handleValidate = async () => {
        try {
            const result = await declaracionRentaService.validateDJ1835Data(
                declaracionId,
                idCompany,
                token
            );
            setValidationResult(result);

            if (result.valid) {
                Swal.fire({
                    icon: 'success',
                    title: 'Validación exitosa',
                    text: `Todos los ${result.totalRegistros} registros son válidos`
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Errores de validación',
                    html: `<ul style="text-align: left;">${result.errors.map(e => `<li>${e}</li>`).join('')}</ul>`
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al validar datos'
            });
        }
    };

    const handleDownloadCSV = async () => {
        if (registros.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin datos',
                text: 'No hay registros para generar el CSV'
            });
            return;
        }

        try {
            // Primero validar
            const validation = await declaracionRentaService.validateDJ1835Data(
                declaracionId,
                idCompany,
                token
            );

            if (!validation.valid) {
                const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Errores de validación',
                    html: `Se encontraron errores:<br/><ul style="text-align: left;">${validation.errors.map(e => `<li>${e}</li>`).join('')}</ul><br/>¿Desea descargar el CSV de todas formas?`,
                    showCancelButton: true,
                    confirmButtonText: 'Sí, descargar',
                    cancelButtonText: 'Cancelar'
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            // Descargar CSV
            await declaracionRentaService.downloadAndSaveDJ1835CSV(
                declaracionId,
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: 'El archivo CSV se ha descargado correctamente'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al descargar CSV'
            });
        }
    };

    const getMesesSeleccionados = (registro) => {
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                       'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        return meses.filter(mes => registro[mes]).length;
    };

    return (
        <div className="dj1835-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaBuilding className="title-icon" />
                    <div>
                        <h2>DJ 1835 - Bienes Raíces Arrendados</h2>
                        <p className="subtitle">Año {year} | Total: {registros.length} registros</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-validate"
                        onClick={handleValidate}
                        disabled={loading || registros.length === 0}
                    >
                        <FaCheckCircle /> Validar Datos
                    </button>
                    <button
                        className="btn-download"
                        onClick={handleDownloadCSV}
                        disabled={loading || registros.length === 0}
                    >
                        <FaFileDownload /> Descargar CSV
                    </button>
                    <button
                        className="btn-nuevo"
                        onClick={() => handleOpenModal()}
                        disabled={loading}
                    >
                        <FaPlus /> Nuevo Registro
                    </button>
                </div>
            </div>

            {/* Información del formato CSV */}
            <div className="info-box">
                <h4>Formato CSV según SII:</h4>
                <ul>
                    <li>Separador: punto y coma (;)</li>
                    <li>RUT sin guiones, K en mayúscula</li>
                    <li>Números sin separadores de miles</li>
                    <li>Encoding: UTF-8 (MS-DOS)</li>
                </ul>
            </div>

            {/* Tabla de registros */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : registros.length === 0 ? (
                    <div className="empty-message">
                        <FaBuilding className="empty-icon" />
                        <p>No hay bienes raíces arrendados registrados</p>
                        <button className="btn-primary" onClick={() => handleOpenModal()}>
                            <FaPlus /> Agregar Primer Registro
                        </button>
                    </div>
                ) : (
                    <table className="registros-table">
                        <thead>
                            <tr>
                                <th>N°</th>
                                <th>ROL Bien Raíz</th>
                                <th>Comuna</th>
                                <th>RUT Arrendador</th>
                                <th>RUT Arrendatario</th>
                                <th>Monto Arriendo</th>
                                <th>Meses</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map((registro) => (
                                <tr key={registro.id}>
                                    <td>{registro.numero}</td>
                                    <td>{registro.rol_bien_raiz}</td>
                                    <td>{registro.comuna_propiedad}</td>
                                    <td>{registro.rut_arrendador}</td>
                                    <td>{registro.rut_arrendatario}</td>
                                    <td>${parseInt(registro.monto_arriendo).toLocaleString('es-CL')}</td>
                                    <td>{getMesesSeleccionados(registro)} meses</td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleOpenModal(registro)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(registro)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingRegistro ? 'Editar' : 'Nuevo'} Bien Raíz Arrendado</h3>
                            <button className="btn-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-sections">
                                {/* Datos del Bien Raíz */}
                                <div className="form-section">
                                    <h4>Datos del Bien Raíz</h4>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="rol_bien_raiz">ROL del Bien Raíz *</label>
                                            <input
                                                type="text"
                                                id="rol_bien_raiz"
                                                name="rol_bien_raiz"
                                                value={formData.rol_bien_raiz}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 12345-6"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="comuna_propiedad">Comuna de la Propiedad *</label>
                                            <input
                                                type="text"
                                                id="comuna_propiedad"
                                                name="comuna_propiedad"
                                                value={formData.comuna_propiedad}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: Santiago"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Datos del Arriendo */}
                                <div className="form-section">
                                    <h4>Datos del Arriendo</h4>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="rut_arrendador">RUT Arrendador *</label>
                                            <input
                                                type="text"
                                                id="rut_arrendador"
                                                name="rut_arrendador"
                                                value={formData.rut_arrendador}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 12345678-9"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="rut_arrendatario">RUT Arrendatario *</label>
                                            <input
                                                type="text"
                                                id="rut_arrendatario"
                                                name="rut_arrendatario"
                                                value={formData.rut_arrendatario}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 98765432-1"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="monto_arriendo">Monto Arriendo Mensual *</label>
                                            <input
                                                type="number"
                                                id="monto_arriendo"
                                                name="monto_arriendo"
                                                value={formData.monto_arriendo}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ej: 500000"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Meses de Arriendo */}
                                <div className="form-section">
                                    <h4>Meses de Arriendo</h4>
                                    <div className="meses-grid">
                                        {['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                                          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'].map(mes => (
                                            <div className="checkbox-group" key={mes}>
                                                <input
                                                    type="checkbox"
                                                    id={mes}
                                                    name={mes}
                                                    checked={formData[mes]}
                                                    onChange={handleInputChange}
                                                />
                                                <label htmlFor={mes}>{mes.charAt(0).toUpperCase() + mes.slice(1)}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Características Adicionales */}
                                <div className="form-section">
                                    <h4>Características Adicionales</h4>
                                    <div className="form-grid">
                                        <div className="checkbox-group">
                                            <input
                                                type="checkbox"
                                                id="amoblado"
                                                name="amoblado"
                                                checked={formData.amoblado}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="amoblado">Amoblado</label>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="destino_arriendo">Destino del Arriendo</label>
                                            <select
                                                id="destino_arriendo"
                                                name="destino_arriendo"
                                                value={formData.destino_arriendo}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Seleccione...</option>
                                                <option value="HAB">Habitacional</option>
                                                <option value="COM">Comercial</option>
                                                <option value="IND">Industrial</option>
                                                <option value="OTR">Otro</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="naturaleza_bien_raiz">Naturaleza del Bien Raíz</label>
                                            <select
                                                id="naturaleza_bien_raiz"
                                                name="naturaleza_bien_raiz"
                                                value={formData.naturaleza_bien_raiz}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Seleccione...</option>
                                                <option value="URB">Urbano</option>
                                                <option value="RUR">Rural</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="marque_con_x">Marque con X</label>
                                            <input
                                                type="text"
                                                id="marque_con_x"
                                                name="marque_con_x"
                                                value={formData.marque_con_x}
                                                onChange={handleInputChange}
                                                placeholder="Campo especial SII"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingRegistro ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DJ1835BienesRaicesTab;
